import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import { DateTime } from 'luxon';
import React, { ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { CampaignType } from 'apis/restaurant-api/merchant-admin/campaigns/types';
import { Typography } from 'common/typography';

import { SCHEDULING_TIMEFRAMES } from './constants';
import { SchedulingTimeframe } from './types';
import { getDuration } from './utils';
import CampaignFormItem from '../CampaignFormItem';
import { HelperText } from '../HelperText';
import { VenueCampaignOverlapAlert } from '../VenueCampaignOverlapAlert';

export type SchedulingDetailsProps = {
  startDate: string;
  endDate: string;
  timezone: string;
  timeframe: SchedulingTimeframe;
  action?: ReactNode;
  country: Country;
  isEndOfDayCampaign?: boolean;
  campaignType?: CampaignType;
  checkVenueCampaignOverlap?: boolean;
  'data-testid'?: string;
};

const StyledTwoColumns = styled.div`
  display: flex;
  gap: 16px;
  padding-block-start: 10px;
`;

const StyledColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StyledHeader = styled(Typography.Body3)`
  color: ${cbSemanticCssVars.textSecondary};
  min-width: 64px;
`;

const StyledVenueCampaignOverlapAlert = styled(VenueCampaignOverlapAlert)`
  margin-block-start: 12px;
`;

export const SchedulingDetails = ({
  startDate,
  endDate,
  timezone,
  action,
  timeframe,
  country,
  isEndOfDayCampaign,
  campaignType,
  checkVenueCampaignOverlap,
  ...props
}: SchedulingDetailsProps) => {
  const { locale, formatMessage } = useIntl();
  const startDateObj = DateTime.fromISO(startDate).set({ second: 0, millisecond: 0 }).setZone(timezone);
  const endDateObj = DateTime.fromISO(endDate).set({ second: 0, millisecond: 0 }).setZone(timezone);
  const durationFormat = getDuration(startDate, endDate, timezone);
  const timeframeLabel = SCHEDULING_TIMEFRAMES.find(_timeframe => _timeframe.value === timeframe)?.label;

  return (
    <CampaignFormItem
      data-testid={props['data-testid']}
      action={action}
      title={<FormattedMessage id="campaigns.form.scheduling.label" />}
      subtitle={isEndOfDayCampaign && <FormattedMessage id="campaigns.form.scheduling.end-of-day-subtitle" />}
      value={
        <>
          {checkVenueCampaignOverlap && !!campaignType && (
            <StyledVenueCampaignOverlapAlert campaignType={campaignType} startDate={startDate} endDate={endDate} />
          )}
          <StyledTwoColumns>
            <StyledColumn>
              <StyledHeader fontWeight="semiBold">
                <FormattedMessage id="campaigns.form.scheduling.start.label" />
              </StyledHeader>
              <StyledHeader fontWeight="semiBold">
                <FormattedMessage id="campaigns.form.scheduling.end.label" />
              </StyledHeader>
              <StyledHeader fontWeight="semiBold">
                <FormattedMessage id="campaigns.form.scheduling.duration.label" />
              </StyledHeader>
              <StyledHeader fontWeight="semiBold">
                <FormattedMessage id="campaigns.form.scheduling.running-times.label" />
              </StyledHeader>
            </StyledColumn>

            <StyledColumn>
              <Typography.Body3>{startDateObj.toLocaleString(DateTime.DATETIME_SHORT, { locale })}</Typography.Body3>
              <Typography.Body3>{endDateObj.toLocaleString(DateTime.DATETIME_SHORT, { locale })}</Typography.Body3>
              <Typography.Body3>
                {durationFormat.value?.toLocaleString(locale, {
                  style: 'unit',
                  unit: durationFormat.unit,
                  unitDisplay: 'long',
                })}
              </Typography.Body3>
              <Typography.Body3>{timeframeLabel ? <FormattedMessage id={timeframeLabel} /> : '-'}</Typography.Body3>
            </StyledColumn>
          </StyledTwoColumns>

          <HelperText text={formatMessage({ id: 'campaigns.form.scheduling.helper-text' })} />
          {country === 'ISR' && (
            <HelperText text={formatMessage({ id: 'campaigns.form.scheduling.helper-determine-text' })} />
          )}
        </>
      }
    />
  );
};
